import React, { FC, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';

import { getFormattedDate } from '@utils/date';
import {
  // convertToUSD,
  // formatAmount,
  formatAmountInDollars,
  getNumberFromString
} from '@utils/amount';
import { DeleteIcon } from '@assets/icons';
import { ToastTypes } from '@type/toast';
import { translate } from '@utils/locale';
import { showGenericErrorToast } from '@utils/error';
import { ValuationTypes } from '@constants/common';
import displayToast from '@components/toast/customToast';
import useTimeZone from '@hooks/useTimeZone';
import CustomButton from '@components/custom-button/CustomButton';

import { TimeLineData, calculateGrowth } from '../services/rounds';
import { useDeleteRoundMutation, useDeleteValuationMutation } from '../api';
import { RoundType } from '../types';
import ValuationTimelineStepper from '../components/valuation-timeline-stepper/ValuationTimelineStepper';
import AddRoundModal from './AddRoundModal';
import useGetRounds from '../hooks/useGetRounds';
import RoundDetailsCard from '../components/round-details-card/RoundDetailsCard';
import ValuationUpdateCard from '../components/valuation-update-card/ValuationUpdateCard';

// todo : update investor count, growth percentage

const ValuationsTimeline: FC<{ disableEdit?: boolean }> = ({
  disableEdit = false
}) => {
  const { roundsTimelineData } = useGetRounds();
  const { getDateFromUtcDateWithTimeZone } = useTimeZone();

  const [deleteRound, { isLoading: isDeleteRoundLoading }] =
    useDeleteRoundMutation();
  const [deleteValuation, { isLoading: isDeleteValuationLoading }] =
    useDeleteValuationMutation();

  const [editRoundDetails, setEditRoundDetails] = useState<TimeLineData | null>(
    null
  );

  const { companyId } = useParams();

  const { data: RoundData } = useGetRounds();

  const roundDetailsList =
    RoundData?.rounds
      ?.filter(
        round => round.valuation_type === editRoundDetails?.valuationType
      )
      .map(round => round.round_name) || [];

  const setRoundDetails = (data: TimeLineData) => {
    setEditRoundDetails(data);
  };

  const onCloseAddRoundModal = () => {
    setEditRoundDetails(null);
  };

  const onClickDelete = async (type: RoundType, id: string) => {
    if (type === RoundType.VALUATION_UPDATE) {
      try {
        const res = await deleteValuation({
          company_id: companyId ?? '',
          valuation_id: id
        }).unwrap();

        if (res.result === 'SUCCESS') {
          displayToast(
            ToastTypes.SUCCESS,
            'Successfully deleted valuation update'
          );
        } else {
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to delete valuation update'
          );
        }
      } catch (err) {
        showGenericErrorToast(err);
      }
    } else {
      try {
        const res = await deleteRound({
          company_id: companyId ?? '',
          round_id: id
        }).unwrap();

        if (res.result === 'SUCCESS') {
          displayToast(ToastTypes.SUCCESS, 'Successfully deleted round');
        } else {
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to delete round'
          );
        }
      } catch (err) {
        showGenericErrorToast(err);
      }
    }
  };

  const renderRounds = (item: TimeLineData, index: number) => {
    if (item.type === RoundType.VALUATION_UPDATE) {
      const growth = calculateGrowth(
        roundsTimelineData.slice(index + 1),
        parseInt(getNumberFromString(item.valuation as string))
      );

      return (
        <ValuationTimelineStepper
          hideLine={roundsTimelineData.length - 1 === index}>
          <div className="flex relative justify-between">
            <div className="mb-10 w-[80%] !max-w-[80%]">
              <ValuationUpdateCard
                data={[
                  {
                    label: translate('company.valuationTimeline.roundDate'),
                    value: getFormattedDate(
                      getDateFromUtcDateWithTimeZone(item.roundDate),
                      'DD MMM YY'
                    )
                  },
                  {
                    label: translate('company.valuationTimeline.valuation'),
                    value: formatAmountInDollars(
                      parseInt(getNumberFromString(item.valuation as string))
                    )
                  }
                ]}
                growth={growth}
              />
            </div>
            {!disableEdit && (
              <div className="flex absolute top-0 right-0  justify-center items-center h-fit">
                {isDeleteValuationLoading ? (
                  <CircularProgress size="1rem" />
                ) : (
                  <DeleteIcon
                    onClick={() => onClickDelete(item.type, item.id)}
                    className="cursor-pointer delete-icon"
                  />
                )}
                <CustomButton
                  variant="contained"
                  fullWidth={false}
                  onClick={() => setRoundDetails(item)}
                  label={translate('edit')}
                  customClassname="transition-transform transform hover:bg-gradient-to-r hover:from-denimBlue hover:to-denimBlue hover:border-2 hover:border-blue-500 hover:shadow-xl"
                />
              </div>
            )}
          </div>
        </ValuationTimelineStepper>
      );
    }
    return (
      <ValuationTimelineStepper
        hideLine={roundsTimelineData.length - 1 === index}>
        <div className="flex relative justify-between w-full">
          <div className="mb-5 w-full !max-w-full">
            <RoundDetailsCard
              data={[
                {
                  label: translate('company.valuationTimeline.roundDate'),
                  value: getFormattedDate(item.roundDate, 'DD MMM YY')
                },
                {
                  label:
                    item.valuationType === ValuationTypes.PRICED
                      ? translate('company.valuationTimeline.valuation')
                      : translate('company.valuationTimeline.convertibleTerms'),
                  value:
                    item.valuationType === ValuationTypes.PRICED
                      ? formatAmountInDollars(
                          parseInt(
                            getNumberFromString(item.valuation as string)
                          )
                        )
                      : item.convertibleTerms
                },
                {
                  label: translate('company.valuationTimeline.totalRoundSize'),
                  value: formatAmountInDollars(
                    item.size ? Number(item.size) : 0
                  )
                },
                {
                  label: translate('company.valuationTimeline.investors'),
                  value: item.investorCount ? String(item.investorCount) : '0'
                }
              ]}
              companyStage={item.companyStage}
              roundId={item.id}
              roundName={item.roundName}
              disableEdit={disableEdit}
            />
          </div>
          {!disableEdit && (
            <div className="flex absolute top-0 right-0 justify-center items-center h-fit">
              {isDeleteRoundLoading ? (
                <CircularProgress size="1rem" />
              ) : (
                <DeleteIcon
                  onClick={() => onClickDelete(item.type, item.id)}
                  className="mr-2 cursor-pointer"
                />
              )}
              <CustomButton
                variant="contained"
                fullWidth={false}
                onClick={() => setRoundDetails(item)}
                label={translate('company.valuationTimeline.edit')}
                customClassname="transition-transform transform hover:bg-gradient-to-r hover:from-[#1769aa] hover:to-[#1769aa] hover:border-2 hover:border-blue-500 hover:shadow-xl"
              />
            </div>
          )}
        </div>
      </ValuationTimelineStepper>
    );
  };

  return (
    <>
      <div className="my-5">
        {roundsTimelineData.map((item, index) => renderRounds(item, index))}
      </div>
      {!!editRoundDetails && (
        <AddRoundModal
          handleCloseModal={onCloseAddRoundModal}
          isOpen={!!editRoundDetails}
          valuationType={editRoundDetails?.valuationType ?? ''}
          isRoundDetailsRequired={editRoundDetails?.type === RoundType.ROUND}
          roundDetails={editRoundDetails ? editRoundDetails : undefined}
          existingRoundNames={roundDetailsList}
        />
      )}
    </>
  );
};

export default ValuationsTimeline;
